import { useSelector } from 'react-redux';

import { ScopeCode } from 'config/scopes';
import { PermissionMapModel, RouteConfigurationModel } from 'models';
import { selectUserPermissions } from 'store/profile';

export type ScopeMode = 'view' | 'edit';

export interface AccessRestriction {
  scope?: ScopeCode;
  mode?: ScopeMode;
}

export const hasPermission = (permissions?: PermissionMapModel, restriction?: AccessRestriction) => {
  const { scope, mode = 'view' } = restriction ?? {};

  if (!scope) {
    return true;
  }

  if (!permissions) {
    return false;
  }

  const grant = permissions[scope];

  if (!grant) {
    return false;
  }

  return mode === 'edit' ? grant.edit : grant.view;
};

export const filterRoutes = (
  routes: RouteConfigurationModel[],
  permissions?: PermissionMapModel
) => {
  if (!Array.isArray(routes)) {
    return [];
  }

  return routes.reduce((filtered, current) => {
    if (hasPermission(permissions, current)) {
      const children = current.children
        ? current.children.filter((route) => hasPermission(permissions, route))
        : [];

      if (!current.scope && current.children?.length && !children.length) {
        return filtered;
      }

      filtered.push({
        ...current,
        children,
      } as RouteConfigurationModel);
    }

    return filtered;
  }, [] as RouteConfigurationModel[]);
};

export const useCanView = (scope: ScopeCode): boolean => {
  const permissions = useSelector(selectUserPermissions);

  return hasPermission(permissions, { scope, mode: 'view' });
};

export const useCanEdit = (scope: ScopeCode): boolean => {
  const permissions = useSelector(selectUserPermissions);

  return hasPermission(permissions, { scope, mode: 'edit' });
};
